// Lightweight, zero-dependency i18n.
//
// Why not i18next / Lingui / FormatJS? They're all great but bring 50-200KB
// of bundle, plurals/ICU formatting we don't need yet, and namespace/loader
// machinery for asset-splitting we won't use. A simple in-memory dict keyed
// by short string IDs ("login.title", "common.save") is enough for the
// platform's UI surface.
//
// Source of truth for the dictionary: src/core/lib/translations.ts.
// Every UI string the user sees should be a t('key') call so a new
// language is dropped in by extending one map.
//
// Persistence:
//   - localStorage 'lang' is the cold-start source. Survives logout.
//   - When the user is logged in, the LanguageSwitcher also PATCHes
//     /me with { language } so the choice follows them across devices.
//   - When /me returns a non-default `language`, App's <LanguageSync/>
//     mirrors it back to localStorage so the same browser stays in sync.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { TRANSLATIONS, SUPPORTED_LANGS, DEFAULT_LANG } from './translations';
import type { Lang } from './translations';

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isRTL: boolean;
  /**
   * Has the user EXPLICITLY chosen a language yet? True iff a value is
   * stored in localStorage ('lang') — either from a prior session or
   * because setLang() was called this session. `lang` alone can't tell
   * this apart (an un-chosen user still resolves to DEFAULT_LANG), so a
   * first-run language picker keys off `chosen` to decide whether to
   * force a selection before the app is usable.
   */
  chosen: boolean;
};

const I18nContext = createContext<Ctx | null>(null);

const STORAGE_KEY = 'lang';
const RTL_LANGS: Lang[] = ['ur'];

function readSavedLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED_LANGS.some((l) => l.code === v)) return v as Lang;
  } catch { /* localStorage unavailable */ }
  return DEFAULT_LANG;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readSavedLang());
  // Whether a language has ever been explicitly stored on this device.
  const [chosen, setChosen] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) != null; } catch { return false; }
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    setChosen(true);
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* noop */ }
  }, []);

  // Keep <html lang="…"> + dir="rtl|ltr" in sync. Browsers and assistive
  // tech use these to pick fonts and reading direction; without it the
  // Urdu rendering would look ragged and screen readers would speak
  // non-Latin text as if it were English.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
  }, [lang]);

  const t = useCallback((key: string, params?: Record<string, string | number>) => {
    const dict = TRANSLATIONS[lang] ?? {};
    const fallback = TRANSLATIONS[DEFAULT_LANG];
    let s = dict[key] ?? fallback[key] ?? key;
    if (params) {
      // Tiny {placeholder} interpolation. No plural/ICU rules — keep simple.
      for (const k of Object.keys(params)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(params[k]));
      }
    }
    return s;
  }, [lang]);

  const value = useMemo<Ctx>(() => ({ lang, setLang, t, isRTL: RTL_LANGS.includes(lang), chosen }), [lang, setLang, t, chosen]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): Ctx {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n must be used inside <I18nProvider>');
  return v;
}

// Convenience: most call-sites only need the translator function itself.
export function useT() {
  return useI18n().t;
}
